import { useEffect, useMemo, useState } from "react"
import { decryptData, encryptData } from "../../../utils/encrypt";
import services from "../../../services/services";
import _ from "underscore";
import { dateConvert, formatDateObject } from "../../../utils/Helper";
import { DateObject } from "react-multi-date-picker";

export const useFailSqlJobs = ({ projectChanged }) => {

    const [jobData, setJobData] = useState({ x_axis: [], y_axis: [], api_data: [] });
    const [showPopup, setShowPopup] = useState(false);
    const [popupData, setPopupData] = useState([]);
    const [hoveredRange, setHoveredRange] = useState([]);
    const [loader, setLoader] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const [filterOpts, setFilterOpts] = useState({ dbname: [] });
    const [filterValues, setFilterValues] = useState({ dbname: [], date: {} });
    const [filterCount, setFilterCount] = useState(0);
    const [expand, setExpand] = useState(false);
    const [time, setTime] = useState({ from: null, to: null });
    const [intervalFlag, setIntervalFlag] = useState(false);
    const [defaultClicked, setDefaultClicked] = useState(true);

    useEffect(() => {
        handleDateRange(null, true);
    }, [])

    useEffect(() => {
        getData()
    }, [projectChanged])

    useEffect(() => {
        const interval = setInterval(() => {
            if (defaultClicked) {
                handleDateRange(null, true);
            } else {
                getData();
            }
        }, 120000);

        return () => clearInterval(interval);
    }, [intervalFlag]);

    const handleDateRange = (e, defaultFlag) => {
        if (e === null) {
            const now = new DateObject();
            const oneMinuteAgo = new DateObject().subtract(1, "hour");
            const range = [new DateObject(oneMinuteAgo), new DateObject(now)];
            setHoveredRange(range);
            setTime({ from: oneMinuteAgo?.toDate(), to: now?.toDate() });
            defaultFlag && handleApplyFilters(range) && setDefaultClicked(true);
        } else {
            setHoveredRange(e);
            setDefaultClicked(false);
        }
    }

    const handleApplyFilters = (dateRange) => {
        let dates;
        if (dateRange) {
            dates = {
                from_date: dateConvert(dateRange?.[0]?.toDate(), "iso-string")?.split(".")?.[0],
                to_date: dateConvert(dateRange?.[1]?.toDate(), "iso-string")?.split(".")?.[0]
            }
        } else {
            dates = {
                from_date: formatDateObject(hoveredRange?.[0], time?.from),
                to_date: formatDateObject(hoveredRange?.[1], time?.to)
            }
        }
        const serviceFilters = {
            dates: dates,
            dbname: filterValues?.dbname,
        }
        sessionStorage.setItem("jobsfilters", encryptData(JSON.stringify(serviceFilters)));

        // set count of filters applied
        let count = 0;
        if ((hoveredRange === undefined || _.isEmpty(hoveredRange)) && (dateRange === undefined || _.isEmpty(dateRange))) {
            count = 0;
        } else {
            count++;
        }
        Object.keys(filterValues)?.forEach((item) => {
            if (filterValues[item].length > 0) {
                count++;
            }
        })
        setFilterCount(count);
        setShowFilter(false);
        sessionStorage.getItem("inspectra_error_view") && getData();
        setIntervalFlag(!intervalFlag);
    }

    const handleDefaultFilters = () => {
        handleDateRange(null, false);
        setDefaultClicked(true);
        setFilterValues((prev) => ({ ...prev, dbname: [] }));
    }

    const getData = async () => {
        try {
            const selectedProject = sessionStorage.getItem("inspectra_project") ? JSON.parse(decryptData(sessionStorage.getItem("inspectra_project"))) : {};
            if (!_.isEmpty(selectedProject) && selectedProject?.host) {
                setLoader(true);
                const filters = JSON.parse(decryptData(sessionStorage.getItem("jobsfilters")) || "{}");
                setFilterValues((prev) => ({ ...prev, date: filters?.dates }));
                const payload = {
                    "from_date": filters?.dates?.from_date,
                    "to_date": filters?.dates?.to_date,
                    "dbname": _.isEmpty(filters?.dbname) ? undefined : filters?.dbname?.toString(),
                    "project_name": selectedProject?.project_key
                }

                const hostArray = _.pluck(selectedProject?.host, "domain_host_key");
                const responses = await Promise.all(
                    hostArray.map(async (host) => {
                        try {
                            const response = await services.callInspectra("GET", '/inspectra/jobdetails/v1', payload, host);
                            return response?.data?.[0];
                        } catch (err) {
                            console.error(`Error fetching data for host ${host}`, err);
                            return null;
                        }
                    })
                );

                const dataArray = responses.filter(Boolean).flat();

                if (dataArray) {
                    const data = dataArray;
                    setJobData((prev) => ({ ...prev, api_data: data }));
                    let job = _.uniq(_.pluck(data, "jobname"));
                    let dbname = _.uniq(_.pluck(data, "dbname"));
                    if (_.isEmpty(filters?.dbname)) {
                        setFilterOpts((prev) => ({ ...prev, dbname: dbname }))
                    }
                    let arr = [];
                    job?.forEach((job) => {
                        dbname?.forEach((db) => {
                            const find = _.filter(data, { jobname: job, dbname: db });
                            arr.push(find?.length);
                        })
                    })
                    const chartData = arr.map((y, index) =>
                        index >= 0 && index < job.length ? { x: index, y: y, z: 1 } : null
                    );
                    setJobData((prev) => ({ ...prev, x_axis: job, y_axis: chartData }));
                    setLoader(false);
                }
            }
        } catch (error) {
            console.error("Error: ", error);
            setLoader(false);
        }
    }

    const handleShowDetailsPopup = (job) => {
        const data = _.filter(jobData?.api_data, { jobname: job })?.sort((a, b) => { return new Date(b?.rundatetime_ist) - new Date(a?.rundatetime_ist) })
        setPopupData(data);
        setShowPopup(true);
    }

    const options = {
        credits: { enabled: false },
        chart: {
            type: 'bubble',
            inverted: true,
            events: {
                render: function () {
                    const chart = this;
                    chart.xAxis[0].ticks &&
                        Object.values(chart.xAxis[0].ticks).forEach(tick => {
                            if (tick.label && !tick.label.clickAttached && isNaN(tick.label.textStr)) {
                                tick.label.element.style.cursor = 'pointer';
                                tick.label.element.onclick = () => {
                                    handleShowDetailsPopup(tick.label.textStr);
                                };
                                tick.label.clickAttached = true;
                            }
                        });
                }
            }
        },
        title: { text: '' },
        xAxis: {
            categories: jobData?.x_axis,
            min: 0,
            labels: {
                style: {
                    width: '100px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }
            },
            title: { text: 'Jobs' },
            gridLineDashStyle: 'solid',
            gridLineWidth: 1
        },
        yAxis: {
            tickInterval: 1,
            title: { text: 'Failed Count' }
        },
        tooltip: {
            formatter: function () {
                return `<b>${jobData?.x_axis[this.point.x]}</b><br/>Failed Count: <b>${this.point.y}</b>`;
            }
        },
        plotOptions: {
            series: {
                cursor: 'pointer',
                point: {
                    events: {
                        click: function () {
                            const name = jobData?.x_axis[this.x];
                            handleShowDetailsPopup(name);
                        }
                    }
                },
                enableMouseTracking: true,
                showInLegend: false
            }
        },
        series: [{
            name: 'Failed Count',
            data: jobData?.y_axis
        }]
    };

    return useMemo(() => ({
        options, showPopup, setShowPopup, popupData, hoveredRange, setHoveredRange, loader, showFilter, setShowFilter, filterOpts, filterValues, setFilterValues, filterCount, expand, setExpand, time, setTime, handleDateRange, handleApplyFilters, handleDefaultFilters
    }), [options, showPopup, setShowPopup, popupData, hoveredRange, setHoveredRange, loader, showFilter, setShowFilter, filterOpts, filterValues, setFilterValues, filterCount, expand, setExpand, time, setTime, handleDateRange, handleApplyFilters, handleDefaultFilters])
}